import type { JSONContent } from '@tiptap/core';
import type React from 'react';

import type { ModalService } from './../ricos-modals';
import type { EditorPluginConfig } from './../ricos-types';

import type { DiscardPayload } from './discard-confirmation-modal/discard-confirmation-modal';

export const AI_TYPE = 'wix-rich-content-plugin-ai';
export const AI_MODAL_ID = `${AI_TYPE}.modal`;
export const AI_DISCARD_MODAL_ID = `${AI_TYPE}.discardModal`;
export const AI_ACTIONS_MODAL_ID = `${AI_TYPE}.actionsModal`;
export const AI_FLOW_SELECTION_MODAL_ID = `${AI_TYPE}.flowSelectionModal`;
export const AI_IMAGE_MODAL_ID = `${AI_TYPE}.imageModal`;

export interface AiPluginEditorConfig extends EditorPluginConfig {
  configurePrompts?: (prompts: PromptsConfiguration) => PromptsConfiguration;
  getGatewayBaseUrl?: () => string;
  appDefId: string;
  /*
   * Exposes an AI button for generating image captions.
   *
   * @default true
   */
  enableImageCaptionExport?: boolean;
  /*
   * Exposes an AI button for generating image alt text.
   *
   * @default true
   */
  enableImageAltTextExport?: boolean;
  showToolbarButtonText?: boolean;
  enableImageGeneration?: boolean;
}

export type Prompt = {
  type: 'prompt';
  title: string;
  icon?: React.FC;
  sortResultActionsFn?: (
    a: ResultsActionsCollectionItem,
    b: ResultsActionsCollectionItem
  ) => number;
} & (
  | {
      /** Referencing promptId from prompt-hub */
      promptId: string;
      promptParams?: Record<string, string>;
    }
  | {
      /** Referencing promptSlug from built in ricos prompts */
      promptSlug: string;
      promptParams?: Record<string, string>;
    }
);

type Section = {
  type: 'section';
  title: string;
};

export type PromptCollection = (Prompt | Section)[];

export type PromptsConfiguration = {
  selectionPrompts: PromptCollection;
  floatingToolbarButtonPrompts: PromptCollection;
};

export type ResultAction = {
  name: string;
  type: 'action';
  title: string;
  icon?: React.FC;
  onClick: (entry: ResultEntry) => any;
};

export type ResultsActionsCollectionItem = ResultAction | Section;
export type ResultsActionsCollection = ResultsActionsCollectionItem[];

export type ResultContent = {
  content: JSONContent; // Using content to display it in modal insert it in editor
  plainContent: string; // Using plainContent to copy it to clipboard
  htmlContent: string; // Using htmlContent to pass it down to API
};

type RequestEntry = {
  type: 'request';
  requestPrompt: Prompt | string;
};

export type ResultEntry = {
  type: 'result';
  response: ResultContent;
  requestPrompt: Prompt | string;
};

export type Entry = RequestEntry | ResultEntry;

export type InitialPromptOptions = {
  prompt: Prompt;
  placeholder?: string;
  emptyStateTitle?: string;
  emptyStateText?: string;
  autoTrigger: boolean;
  contextualResultActions?: ResultsActionsCollection;
  promptPayloadFormatter?: (prompt: Prompt, textualPrompt: string) => Prompt;
  isSubmitButtonVisible?: () => boolean;
};

export type ReferenceElement = { focus(): void } | null;

export type OpenDiscardModalFn = (
  modalsService: ModalService,
  referenceElement: ReferenceElement
) => Promise<DiscardPayload>;
